import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react-native';
import { useCallback, useRef, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import {
  fitContainer,
  ResumableZoom,
  useImageResolution,
  type ResumableZoomRefType,
} from 'react-native-zoom-toolkit';

import { PreviewActionButton } from './PreviewActionButton';

import { HStack } from '@/components/ui/hstack';
import { Image } from '@/components/ui/image';
import { Spinner } from '@/components/ui/spinner';
import { View } from '@/components/ui/view';

const MAX_SCALE = 6;

/** Each press of the zoom buttons scales by this much, so a few presses reach the maximum. */
const ZOOM_STEP = 1.5;

type Size = { width: number; height: number };

type ZoomableImageProps = {
  uri: string;
  name: string;
  /** How far the zoom buttons sit above the bottom edge, so they clear the home indicator. */
  controlsBottomInset?: number;
};

/**
 * An image fitted whole inside the space it is given, which it fills from edge
 * to edge by positioning itself over its whole container. It can be pinched,
 * double-tapped and dragged to zoom and pan. Pinch and drag are gestures a
 * mouse or a switch user cannot make, so the same zoom is on buttons too.
 *
 * The viewport clips what it zooms, so a zoomed picture cannot spill past the
 * container. The zoom buttons float over its bottom edge, and the caller draws
 * anything else, such as a header, over it.
 *
 * The zoomable child needs a concrete size, so the picture's own proportions
 * are read first and fitted to the measured viewport. Until both are known a
 * spinner stands in, which is why the loading state holds no hidden content.
 * The library's hook keeps the last proportions while it reads again for a
 * refreshed signed URL, so the zoomed picture stays up and is not reset.
 */
export function ZoomableImage({ uri, name, controlsBottomInset = 16 }: ZoomableImageProps) {
  const zoomRef = useRef<ResumableZoomRefType>(null);

  const [viewport, setViewport] = useState<Size | null>(null);

  const { resolution, error } = useImageResolution({ uri });

  /*
   * A picture reporting a zero side has no usable proportions, so it takes the
   * same fallback as one whose size cannot be read: fit it to the viewport and
   * let resizeMode letterbox it, rather than waiting on a ratio that never comes.
   */
  const aspectRatio =
    resolution && resolution.width > 0 && resolution.height > 0
      ? resolution.width / resolution.height
      : null;

  const isSizeUnknown = error !== undefined || (resolution !== undefined && aspectRatio === null);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;

    setViewport({ width, height });
  }, []);

  const zoomBy = useCallback((factor: number) => {
    const zoom = zoomRef.current;

    if (zoom) {
      zoom.zoom(zoom.getState().scale * factor);
    }
  }, []);

  const hasViewport = viewport !== null && viewport.width > 0 && viewport.height > 0;

  const resolvedRatio =
    aspectRatio ?? (isSizeUnknown && hasViewport ? viewport.width / viewport.height : null);

  const imageSize = hasViewport && resolvedRatio ? fitContainer(resolvedRatio, viewport) : null;

  return (
    <View className="absolute inset-0 overflow-hidden" onLayout={handleLayout}>
      {imageSize ? (
        <ResumableZoom ref={zoomRef} maxScale={MAX_SCALE}>
          <View style={imageSize}>
            <Image
              source={{ uri }}
              alt={name}
              size="full"
              resizeMode="contain"
              accessibilityIgnoresInvertColors
            />
          </View>
        </ResumableZoom>
      ) : (
        <View className="h-full w-full items-center justify-center">
          <Spinner size="large" accessibilityLabel="Loading image" />
        </View>
      )}

      <HStack
        pointerEvents="box-none"
        className="absolute inset-x-0 justify-center gap-3"
        style={{ bottom: controlsBottomInset }}>
        <PreviewActionButton
          label="Zoom out"
          icon={ZoomOut}
          onPress={() => zoomBy(1 / ZOOM_STEP)}
        />

        <PreviewActionButton label="Zoom in" icon={ZoomIn} onPress={() => zoomBy(ZOOM_STEP)} />

        <PreviewActionButton
          label="Reset zoom"
          icon={Maximize2}
          onPress={() => zoomRef.current?.reset()}
        />
      </HStack>
    </View>
  );
}
